import { BehaviorSubject, distinctUntilChanged, map, shareReplay } from 'rxjs'
import { state } from '@schmancy/state'
import type {
	Feeling,
	FeelingCategory,
	FeelingSound,
	PuffParams,
	SoundTheme,
	SoundThemeSettings,
	ToneParams,
} from '@schmancy/types/mood-audio.types'

// keepOnClear: a device preference, so it survives clearStates() on an
// identity change (a different person signing in must not reset volume/mute).
const SoundContext = state<SoundThemeSettings>('schmancy/sound').local(
	{
		theme: null,
		volume: 0.15,
		muted: false,
	},
	{ keepOnClear: true },
)

// ── the one voice: Heartwood ─────────────────────────────────────────────────
// Every feeling is one or more struck tines of the SAME felt-mallet marimba voice
// as the Owl WAV sound kit — a modal stack (fundamental + marimba overtones that
// decay faster than the body) with a brief kalimba onset and a soft felt-mallet
// transient, on the F major-pentatonic grid. Struck wood can't bend, so contour
// is stepped pitch, never a glide: rising = good/forward, falling = undo/closure,
// flat/low = neutral. Warmth is the mallet lowpass. Reshape the palette here.

type Contour = 'rise' | 'flat' | 'fall'
type Warmth = 'bright' | 'soft' | 'warm' | 'full' | 'low' | 'dark'

interface FeelingSpec {
	// desired register in Hz — snapped to the nearest F-pentatonic degree
	root: number
	// seconds of ring per struck tine
	dur: number
	// struck tines: 1, or >1 for a rising/falling step (or a repeated pulse when flat)
	steps?: number
	// pitch direction = meaning
	contour: Contour
	// mallet warmth (lowpass centroid)
	warmth: Warmth
	// base volume — soft; the service scales by the device volume on top
	vol?: number
	// scale-degrees between struck tines (clamped)
	step?: number
}

// F major-pentatonic grid (F G A C D) across the working registers, in Hz — the
// same tonal home as the WAV cues. Every feeling snaps onto it; nothing off-scale.
const PENTATONIC = [
	174.61,
	196.0,
	220.0,
	261.63,
	293.66, // F3 G3 A3 C4 D4
	349.23,
	392.0,
	440.0,
	523.25,
	587.33, // F4 G4 A4 C5 D5
	698.46,
	783.99,
	880.0, // F5 G5 A5
]
const snapIndex = (hz: number): number => {
	let best = 0
	let bd = Infinity
	for (let i = 0; i < PENTATONIC.length; i++) {
		const d = Math.abs(Math.log2(PENTATONIC[i] / hz))
		if (d < bd) {
			bd = d
			best = i
		}
	}
	return best
}
const pentAt = (i: number): number => PENTATONIC[Math.max(0, Math.min(PENTATONIC.length - 1, i))]

// Mallet warmth → lowpass cutoff on the body (the felt); brighter = higher centroid.
const WARMTH_LP: Record<Warmth, number> = {
	bright: 3200,
	soft: 2200,
	warm: 1400,
	full: 1600,
	low: 1000,
	dark: 700,
}

// One struck heartwood tine in the FeelingSound {puffs, tones} model: a felt-mallet
// noise transient (puff) + a modal stack whose overtones die faster than the
// fundamental + a brief, brighter kalimba onset ping.
function tine(f0: number, start: number, dur: number, vol: number, lp: number): FeelingSound {
	return {
		puffs: [{ startTime: start, duration: 0.02, frequency: Math.min(2600, f0 * 3), volume: vol * 0.5 }],
		tones: [
			{ startTime: start, duration: dur, frequency: f0, volume: vol, type: 'sine', filterFrequency: lp },
			{
				startTime: start,
				duration: dur * 0.5,
				frequency: f0 * 3.92,
				volume: vol * 0.28,
				type: 'sine',
				filterFrequency: lp,
			},
			{
				startTime: start,
				duration: dur * 0.32,
				frequency: f0 * 9.24,
				volume: vol * 0.1,
				type: 'sine',
				filterFrequency: lp,
			},
			{ startTime: start, duration: dur * 0.12, frequency: f0 * 5.0, volume: vol * 0.08, type: 'sine' },
		],
	}
}

function buildFeeling(spec: FeelingSpec): FeelingSound {
	const { root, dur, steps = 1, contour, warmth, vol = 0.2, step = 2 } = spec
	const dir = contour === 'rise' ? 1 : contour === 'fall' ? -1 : 0
	const base = snapIndex(root)
	const lp = WARMTH_LP[warmth]
	const deg = Math.min(3, step)
	// A single strike can't carry a rise/fall (no glide), so a contoured feeling
	// gets at least two stepped strikes; a flat feeling keeps its own pulse count.
	const n = dir !== 0 ? Math.max(2, steps) : steps
	const puffs: PuffParams[] = []
	const tones: ToneParams[] = []
	for (let i = 0; i < n; i++) {
		const voice = tine(pentAt(base + dir * deg * i), i * dur * 0.7, dur, vol, lp)
		puffs.push(...voice.puffs)
		tones.push(...voice.tones)
	}
	return { puffs, tones }
}

// One spec per feeling. Positive families rise and brighten; negative/low families
// fall and darken; tense families stay flat and quick but soft — attention, never
// alarm. The WAV kit's pitch-tells-outcome rule applied across the whole palette.
const FEELING_SPECS: Record<Feeling, FeelingSpec> = {
	// ── happy / pleasant — bright, rising, warm ──
	joyful: { root: 659, dur: 0.14, steps: 3, contour: 'rise', warmth: 'bright', vol: 0.22 },
	content: { root: 523, dur: 0.18, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.2 },
	excited: { root: 659, dur: 0.12, steps: 3, contour: 'rise', warmth: 'bright', vol: 0.22 },
	proud: { root: 523, dur: 0.16, steps: 3, contour: 'rise', warmth: 'full', vol: 0.22 },
	hopeful: { root: 587, dur: 0.26, steps: 1, contour: 'rise', warmth: 'soft', vol: 0.2, step: 4 },
	relieved: { root: 523, dur: 0.24, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.18, step: 2 },
	grateful: { root: 440, dur: 0.2, steps: 3, contour: 'rise', warmth: 'warm', vol: 0.2 },
	peaceful: { root: 392, dur: 0.34, steps: 1, contour: 'flat', warmth: 'warm', vol: 0.15 },
	playful: { root: 659, dur: 0.1, steps: 3, contour: 'rise', warmth: 'bright', vol: 0.2, step: 3 },
	amused: { root: 659, dur: 0.12, steps: 2, contour: 'rise', warmth: 'soft', vol: 0.18 },
	curious: { root: 587, dur: 0.22, steps: 1, contour: 'rise', warmth: 'soft', vol: 0.2, step: 4 },
	inspired: { root: 523, dur: 0.16, steps: 3, contour: 'rise', warmth: 'full', vol: 0.2 },
	confident: { root: 523, dur: 0.18, steps: 2, contour: 'rise', warmth: 'full', vol: 0.22 },
	loved: { root: 440, dur: 0.24, steps: 2, contour: 'rise', warmth: 'full', vol: 0.2, step: 4 },
	comforted: { root: 440, dur: 0.24, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.18, step: 2 },
	energized: { root: 659, dur: 0.12, steps: 3, contour: 'rise', warmth: 'bright', vol: 0.22 },
	celebrated: { root: 523, dur: 0.16, steps: 4, contour: 'rise', warmth: 'bright', vol: 0.2 },

	// ── sad / low — dark, falling, slow ──
	sad: { root: 392, dur: 0.3, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.18 },
	lonely: { root: 330, dur: 0.4, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.15, step: 4 },
	disappointed: { root: 523, dur: 0.3, steps: 1, contour: 'fall', warmth: 'low', vol: 0.18, step: 4 },
	heartbroken: { root: 392, dur: 0.36, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.18 },
	grieving: { root: 349, dur: 0.4, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.16 },
	hopeless: { root: 330, dur: 0.45, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.14 },
	empty: { root: 330, dur: 0.45, steps: 1, contour: 'flat', warmth: 'dark', vol: 0.13 },
	discouraged: { root: 440, dur: 0.3, steps: 1, contour: 'fall', warmth: 'low', vol: 0.16 },
	melancholic: { root: 440, dur: 0.36, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.15 },
	homesick: { root: 440, dur: 0.36, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.14 },
	hurt: { root: 392, dur: 0.34, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.18 },
	miserable: { root: 349, dur: 0.4, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.16 },
	regretful: { root: 440, dur: 0.34, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.15 },
	ashamed: { root: 440, dur: 0.3, steps: 1, contour: 'fall', warmth: 'low', vol: 0.15 },
	inferior: { root: 440, dur: 0.3, steps: 1, contour: 'fall', warmth: 'low', vol: 0.15 },

	// ── anxious / fearful — mid, quick, flat-tense; soft attention, never alarm ──
	anxious: { root: 440, dur: 0.16, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.16 },
	worried: { root: 349, dur: 0.22, steps: 1, contour: 'flat', warmth: 'low', vol: 0.15 },
	afraid: { root: 523, dur: 0.14, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.16 },
	terrified: { root: 587, dur: 0.12, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.18 },
	panicked: { root: 587, dur: 0.1, steps: 3, contour: 'flat', warmth: 'soft', vol: 0.18 },
	nervous: { root: 494, dur: 0.1, steps: 3, contour: 'flat', warmth: 'soft', vol: 0.14 },
	uneasy: { root: 349, dur: 0.22, steps: 1, contour: 'flat', warmth: 'low', vol: 0.14 },
	insecure: { root: 494, dur: 0.1, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.14 },
	overwhelmed: { root: 440, dur: 0.16, steps: 3, contour: 'flat', warmth: 'soft', vol: 0.16 },
	stressed: { root: 440, dur: 0.16, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.16 },
	tense: { root: 440, dur: 0.14, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.16 },
	apprehensive: { root: 349, dur: 0.2, steps: 1, contour: 'flat', warmth: 'low', vol: 0.14 },
	startled: { root: 523, dur: 0.1, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.18 },
	suspicious: { root: 349, dur: 0.22, steps: 2, contour: 'flat', warmth: 'low', vol: 0.14 },
	vulnerable: { root: 494, dur: 0.12, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.13 },

	// ── angry / frustrated — low, firm, flat or falling; firm, not harsh ──
	annoyed: { root: 294, dur: 0.2, steps: 1, contour: 'flat', warmth: 'low', vol: 0.18 },
	irritated: { root: 294, dur: 0.2, steps: 2, contour: 'flat', warmth: 'low', vol: 0.18 },
	frustrated: { root: 349, dur: 0.16, steps: 2, contour: 'fall', warmth: 'low', vol: 0.2 },
	angry: { root: 349, dur: 0.16, steps: 2, contour: 'fall', warmth: 'low', vol: 0.2 },
	enraged: { root: 330, dur: 0.16, steps: 2, contour: 'fall', warmth: 'low', vol: 0.22 },
	bitter: { root: 349, dur: 0.18, steps: 2, contour: 'fall', warmth: 'low', vol: 0.18 },
	resentful: { root: 294, dur: 0.2, steps: 1, contour: 'flat', warmth: 'low', vol: 0.18 },
	jealous: { root: 294, dur: 0.2, steps: 1, contour: 'flat', warmth: 'low', vol: 0.18 },
	envious: { root: 294, dur: 0.2, steps: 1, contour: 'flat', warmth: 'low', vol: 0.18 },
	indignant: { root: 349, dur: 0.16, steps: 2, contour: 'fall', warmth: 'low', vol: 0.2 },
	impatient: { root: 523, dur: 0.08, steps: 3, contour: 'flat', warmth: 'soft', vol: 0.16 },
	hostile: { root: 330, dur: 0.16, steps: 2, contour: 'fall', warmth: 'low', vol: 0.2 },
	contemptuous: { root: 294, dur: 0.2, steps: 1, contour: 'fall', warmth: 'low', vol: 0.18, step: 2 },

	// ── tired / drained — low, slow, falling, very soft ──
	tired: { root: 330, dur: 0.4, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.14 },
	exhausted: { root: 294, dur: 0.5, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.12 },
	drained: { root: 294, dur: 0.5, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.12 },
	burnedOut: { root: 294, dur: 0.5, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.12 },
	numb: { root: 294, dur: 0.5, steps: 1, contour: 'flat', warmth: 'dark', vol: 0.11 },
	bored: { root: 349, dur: 0.3, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.12, step: 2 },
	unmotivated: { root: 349, dur: 0.3, steps: 1, contour: 'fall', warmth: 'dark', vol: 0.12, step: 2 },
	apathetic: { root: 349, dur: 0.3, steps: 1, contour: 'flat', warmth: 'dark', vol: 0.11 },
	restless: { root: 392, dur: 0.14, steps: 3, contour: 'flat', warmth: 'low', vol: 0.14 },

	// ── calm / grounded — low-mid, steady, warm, slow ──
	calm: { root: 392, dur: 0.4, steps: 1, contour: 'flat', warmth: 'warm', vol: 0.13 },
	relaxed: { root: 440, dur: 0.4, steps: 2, contour: 'flat', warmth: 'warm', vol: 0.13 },
	atEase: { root: 440, dur: 0.4, steps: 2, contour: 'flat', warmth: 'warm', vol: 0.13 },
	balanced: { root: 392, dur: 0.4, steps: 1, contour: 'flat', warmth: 'warm', vol: 0.13 },
	stable: { root: 262, dur: 0.4, steps: 1, contour: 'flat', warmth: 'dark', vol: 0.16 },
	secure: { root: 349, dur: 0.34, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.15 },
	safe: { root: 349, dur: 0.34, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.15 },
	centered: { root: 262, dur: 0.4, steps: 1, contour: 'flat', warmth: 'dark', vol: 0.16 },
	grounded: { root: 262, dur: 0.4, steps: 1, contour: 'flat', warmth: 'dark', vol: 0.16 },
	accepting: { root: 392, dur: 0.4, steps: 1, contour: 'flat', warmth: 'warm', vol: 0.13 },

	// ── connected / social — mid, gently rising, warm/full ──
	connected: { root: 440, dur: 0.28, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.16 },
	accepted: { root: 440, dur: 0.24, steps: 2, contour: 'rise', warmth: 'full', vol: 0.18 },
	included: { root: 440, dur: 0.28, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.16 },
	belonging: { root: 440, dur: 0.28, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.16 },
	appreciated: { root: 440, dur: 0.24, steps: 2, contour: 'rise', warmth: 'full', vol: 0.18 },
	valued: { root: 440, dur: 0.24, steps: 2, contour: 'rise', warmth: 'full', vol: 0.18 },
	respected: { root: 440, dur: 0.24, steps: 2, contour: 'rise', warmth: 'full', vol: 0.18 },
	supported: { root: 349, dur: 0.32, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.15 },
	protective: { root: 349, dur: 0.32, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.15 },
	compassionate: { root: 440, dur: 0.28, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.16 },
	empathetic: { root: 440, dur: 0.28, steps: 2, contour: 'rise', warmth: 'warm', vol: 0.16 },

	// ── mixed / complicated — mid, ambiguous; small moves, soft ──
	conflicted: { root: 440, dur: 0.18, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.15 },
	confused: { root: 440, dur: 0.18, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.15 },
	ambivalent: { root: 440, dur: 0.18, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.15 },
	nostalgic: { root: 440, dur: 0.34, steps: 2, contour: 'fall', warmth: 'dark', vol: 0.14 },
	guilty: { root: 440, dur: 0.3, steps: 1, contour: 'fall', warmth: 'low', vol: 0.15 },
	embarrassed: { root: 494, dur: 0.12, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.14 },
	surprised: { root: 659, dur: 0.16, steps: 1, contour: 'rise', warmth: 'bright', vol: 0.2, step: 5 },
	shocked: { root: 587, dur: 0.12, steps: 1, contour: 'rise', warmth: 'soft', vol: 0.18 },
	awestruck: { root: 440, dur: 0.34, steps: 3, contour: 'rise', warmth: 'full', vol: 0.16 },
	skeptical: { root: 440, dur: 0.18, steps: 2, contour: 'flat', warmth: 'soft', vol: 0.15 },
}

const DEFAULT_SOUNDS = Object.fromEntries(
	(Object.entries(FEELING_SPECS) as [Feeling, FeelingSpec][]).map(([feeling, spec]) => [feeling, buildFeeling(spec)]),
) as Record<Feeling, FeelingSound>

class SoundService {
	private static instance: SoundService

	private audioContext: AudioContext | null = null

	// The single bus + gate every voice — synthesized tone and decoded sample
	// alike — connects to before the destination. Its gain IS the service's
	// volume/mute gate, so no sound can bypass the configuration (muting silences
	// even an in-flight cue); the compressor after it brickwalls sums that would
	// otherwise clip.
	private masterGain: GainNode | null = null

	// Sample playback (WAV/MP3/OGG cues loaded by name). Decoded buffers are
	// cached; an in-flight decode is cached as its Promise so concurrent plays
	// share one fetch.
	private readonly sampleUrls = new Map<string, string | (() => string)>()
	private readonly sampleBuffers = new Map<string, AudioBuffer | Promise<AudioBuffer | null>>()

	// Browsers suspend the AudioContext until the first user gesture. `unlocked`
	// flips true once a gesture has resumed it; a cue armed before then plays on
	// unlock (used for the post-sign-in "welcome", which lands with no gesture).
	private unlocked = false
	private pendingUnlockCue: string | null = null

	// theme$ skips distinctUntilChanged (unlike volume$/muted$) — storage-load
	// re-emissions of the same theme object must still notify subscribers.
	public readonly theme$ = SoundContext.$.pipe(
		map(settings => settings.theme),
		shareReplay(1),
	)

	public readonly volume$ = SoundContext.$.pipe(
		map(settings => settings.volume),
		distinctUntilChanged(),
		shareReplay(1),
	)

	public readonly muted$ = SoundContext.$.pipe(
		map(settings => settings.muted),
		distinctUntilChanged(),
		shareReplay(1),
	)

	readonly #themeName$ = new BehaviorSubject<string>('default')
	public readonly themeName$ = this.#themeName$.asObservable()

	get theme(): SoundTheme | null {
		return SoundContext.value.theme
	}

	get volume(): number {
		return SoundContext.value.volume
	}

	get muted(): boolean {
		return SoundContext.value.muted
	}

	get themeName(): string {
		return this.theme?.name ?? 'default'
	}

	private constructor() {
		this.theme$.subscribe(theme => {
			this.#themeName$.next(theme?.name ?? 'default')
		})
		// The persisted volume/mute is the gate; when it changes, ride the master
		// node so the whole service (every synth voice and sample cue) follows.
		this.volume$.subscribe(() => this.applyGate())
		this.muted$.subscribe(() => this.applyGate())
	}

	// Null where there is no Web Audio — jsdom, happy-dom, a browser that refuses
	// to construct a context — so every cue is a no-op there instead of a throw
	// in the code that fired it.
	private getContext(): AudioContext | null {
		if (!this.audioContext) {
			const AudioContextClass: typeof AudioContext | undefined =
				window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
			if (typeof AudioContextClass !== 'function') return null
			try {
				this.audioContext = new AudioContextClass()
			} catch {
				return null
			}
		}
		if (this.audioContext.state === 'suspended') {
			// Without a gesture the resume stays pending, and dispose() closing the
			// context then rejects it: the session ended, nothing failed.
			this.audioContext.resume().catch(() => undefined)
		}
		return this.audioContext
	}

	// The shared gate + limiter bus, built once per context. The GainNode is the
	// service's volume/mute gate (every voice routes through it); the compressor
	// brickwalls sums back under the ceiling.
	private getMaster(ctx: AudioContext): AudioNode {
		if (!this.masterGain) {
			const gain = ctx.createGain()
			gain.gain.value = this.gateLevel()
			const limiter = ctx.createDynamicsCompressor()
			limiter.threshold.value = -1
			limiter.knee.value = 0
			limiter.ratio.value = 20
			limiter.attack.value = 0.003
			limiter.release.value = 0.25
			gain.connect(limiter).connect(ctx.destination)
			this.masterGain = gain
		}
		return this.masterGain
	}

	// The gate value: the device volume, or silence when muted. Per-voice gains
	// carry only the relative mix (params + theme), so this one node governs the
	// loudness and the on/off of everything the service plays.
	private gateLevel(): number {
		return this.muted ? 0 : this.volume
	}

	// Re-apply the gate when the persisted volume/mute changes — smoothly, so it
	// also rides the level of a cue that is already ringing without a click.
	private applyGate(): void {
		if (this.masterGain && this.audioContext) {
			this.masterGain.gain.setTargetAtTime(this.gateLevel(), this.audioContext.currentTime, 0.01)
		}
	}

	private playPuff(ctx: AudioContext, startTime: number, params: PuffParams, mix: number): void {
		const bufferSize = Math.floor(ctx.sampleRate * params.duration)
		const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
		const data = buffer.getChannelData(0)

		for (let i = 0; i < bufferSize; i++) {
			data[i] = Math.random() * 2 - 1
		}

		const noise = ctx.createBufferSource()
		noise.buffer = buffer

		const filter = ctx.createBiquadFilter()
		filter.type = 'bandpass'
		filter.frequency.value = params.frequency
		filter.Q.value = 0.7

		// Clamp the attack so a very short grain still articulates instead of being
		// stopped mid-ramp. `mix` is the relative level (params × theme) — the
		// device volume/mute is applied downstream at the master gate.
		const atk = Math.min(0.015, params.duration * 0.4)
		const gain = ctx.createGain()
		gain.gain.setValueAtTime(0, startTime)
		gain.gain.linearRampToValueAtTime(params.volume * mix, startTime + atk)
		gain.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration)

		noise.connect(filter).connect(gain).connect(this.getMaster(ctx))
		noise.start(startTime)
		noise.stop(startTime + params.duration)
	}

	private playTone(ctx: AudioContext, startTime: number, params: ToneParams, mix: number): void {
		const osc = ctx.createOscillator()
		const gain = ctx.createGain()

		osc.type = params.type
		osc.frequency.setValueAtTime(params.frequency, startTime)

		if (params.frequencyEnd) {
			osc.frequency.exponentialRampToValueAtTime(params.frequencyEnd, startTime + params.duration * 0.8)
		}

		let lastNode: AudioNode = osc
		if (params.filterFrequency) {
			const filter = ctx.createBiquadFilter()
			filter.type = 'lowpass'
			filter.frequency.value = params.filterFrequency
			osc.connect(filter)
			lastNode = filter
		}

		// Clamp the attack so a short tone (e.g. the brief kalimba onset) still
		// articulates instead of being stopped before its ramp completes. `mix` is
		// the relative level; device volume/mute lives at the master gate.
		const atk = Math.min(0.02, params.duration * 0.4)
		gain.gain.setValueAtTime(0, startTime)
		gain.gain.linearRampToValueAtTime(params.volume * mix, startTime + atk)
		gain.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration)

		lastNode.connect(gain).connect(this.getMaster(ctx))
		osc.start(startTime)
		osc.stop(startTime + params.duration)
	}

	public setTheme(theme: SoundTheme): void {
		SoundContext.set({ theme })
	}

	public resetTheme(): void {
		SoundContext.set({ theme: null })
	}

	public setVolume(volume: number): void {
		SoundContext.set({ volume: Math.max(0, Math.min(1, volume)) })
	}

	public mute(): void {
		SoundContext.set({ muted: true })
	}

	public unmute(): void {
		SoundContext.set({ muted: false })
	}

	public toggleMute(): void {
		SoundContext.set({ muted: !this.muted })
	}

	public getSoundForFeeling(feeling: Feeling): FeelingSound {
		const customTheme = this.theme

		if (customTheme?.feelings?.[feeling]) {
			return customTheme.feelings[feeling]!
		}

		if (customTheme?.categoryDefaults) {
			const category = this.getCategoryForFeeling(feeling)
			if (customTheme.categoryDefaults[category]) {
				return customTheme.categoryDefaults[category]!
			}
		}

		return DEFAULT_SOUNDS[feeling]
	}

	private getCategoryForFeeling(feeling: Feeling): FeelingCategory {
		const happyFeelings: Feeling[] = [
			'joyful',
			'content',
			'excited',
			'proud',
			'hopeful',
			'relieved',
			'grateful',
			'peaceful',
			'playful',
			'amused',
			'curious',
			'inspired',
			'confident',
			'loved',
			'comforted',
			'energized',
			'celebrated',
		]
		const sadFeelings: Feeling[] = [
			'sad',
			'lonely',
			'disappointed',
			'heartbroken',
			'grieving',
			'hopeless',
			'empty',
			'discouraged',
			'melancholic',
			'homesick',
			'hurt',
			'miserable',
			'regretful',
			'ashamed',
			'inferior',
		]
		const anxiousFeelings: Feeling[] = [
			'anxious',
			'worried',
			'afraid',
			'terrified',
			'panicked',
			'nervous',
			'uneasy',
			'insecure',
			'overwhelmed',
			'stressed',
			'tense',
			'apprehensive',
			'startled',
			'suspicious',
			'vulnerable',
		]
		const angryFeelings: Feeling[] = [
			'annoyed',
			'irritated',
			'frustrated',
			'angry',
			'enraged',
			'bitter',
			'resentful',
			'jealous',
			'envious',
			'indignant',
			'impatient',
			'hostile',
			'contemptuous',
		]
		const tiredFeelings: Feeling[] = [
			'tired',
			'exhausted',
			'drained',
			'burnedOut',
			'numb',
			'bored',
			'unmotivated',
			'apathetic',
			'restless',
		]
		const calmFeelings: Feeling[] = [
			'calm',
			'relaxed',
			'atEase',
			'balanced',
			'stable',
			'secure',
			'safe',
			'centered',
			'grounded',
			'accepting',
		]
		const connectedFeelings: Feeling[] = [
			'connected',
			'accepted',
			'included',
			'belonging',
			'appreciated',
			'valued',
			'respected',
			'supported',
			'protective',
			'compassionate',
			'empathetic',
		]

		if (happyFeelings.includes(feeling)) return 'happy'
		if (sadFeelings.includes(feeling)) return 'sad'
		if (anxiousFeelings.includes(feeling)) return 'anxious'
		if (angryFeelings.includes(feeling)) return 'angry'
		if (tiredFeelings.includes(feeling)) return 'tired'
		if (calmFeelings.includes(feeling)) return 'calm'
		if (connectedFeelings.includes(feeling)) return 'connected'
		return 'mixed'
	}

	/**
	 * Synthesize a feeling's sound through the shared limiter bus. `dropIfSuspended`
	 * is `playCue`'s guard: voices scheduled on a still-suspended context wait on its
	 * frozen clock and ring at unlock, so a cue whose moment has passed is dropped.
	 */
	public play(feeling: Feeling, opts?: { dropIfSuspended?: boolean }): void {
		if (this.muted) return

		const sound = this.getSoundForFeeling(feeling)
		const ctx = this.getContext()
		if (!ctx || (opts?.dropIfSuspended && ctx.state === 'suspended')) return
		const now = ctx.currentTime
		// Relative mix only (theme); the device volume/mute is the master gate.
		const mix = this.theme?.masterVolume ?? 1

		sound.puffs.forEach(puff => {
			this.playPuff(ctx, now + puff.startTime, puff, mix)
		})
		sound.tones.forEach(tone => {
			this.playTone(ctx, now + tone.startTime, tone, mix)
		})
	}

	// ---- sample cues (WAV/MP3/OGG loaded by name) --------------------------

	/**
	 * Map a cue name to a file URL. Decoding is deferred to the first play, and so is
	 * a URL given as a function: a module evaluated where `import.meta.url` or the
	 * location is non-hierarchical (`about:srcdoc`, `blob:`, `data:`) cannot resolve a
	 * relative asset, and that cue then stays silent instead of failing the import.
	 */
	public registerSample(name: string, url: string | (() => string)): void {
		this.sampleUrls.set(name, url)
	}

	public hasSample(name: string): boolean {
		return this.sampleUrls.has(name)
	}

	private async loadSample(name: string): Promise<AudioBuffer | null> {
		const cached = this.sampleBuffers.get(name)
		if (cached) return cached
		const url = this.sampleUrls.get(name)
		const ctx = this.getContext()
		if (!url || !ctx) return null
		const pending = new Promise<string>(resolve => resolve(typeof url === 'string' ? url : url()))
			.then(resolved => fetch(resolved))
			.then(res => {
				// A 404 resolves rather than rejects — guard so an HTML error body
				// never reaches decodeAudioData as a wasted decode.
				if (!res.ok) throw new Error(`sound sample ${name}: ${res.status}`)
				return res.arrayBuffer()
			})
			.then(buf => ctx.decodeAudioData(buf))
			// A load that outlives dispose() leaves the cache it cleared, and any
			// newer load of the same name, untouched.
			.then(decoded => {
				if (this.sampleBuffers.get(name) === pending) this.sampleBuffers.set(name, decoded)
				return decoded
			})
			.catch(() => {
				// A failed load drops from the cache so a later play can retry.
				if (this.sampleBuffers.get(name) === pending) this.sampleBuffers.delete(name)
				return null
			})
		this.sampleBuffers.set(name, pending)
		return pending
	}

	/** Warm the decode cache without playing — e.g. at boot, off the UI path. */
	public preloadSample(name: string): void {
		void this.loadSample(name)
	}

	/**
	 * Play a registered cue by name through the shared limiter bus.
	 * `dropIfSuspended` is for arrival cues fired from a background push (a new
	 * order landing on an idle tab): if the context is still suspended, a stale
	 * chime seconds later is worse than silence, so drop it rather than queue.
	 */
	public playCue(name: string, opts?: { dropIfSuspended?: boolean }): void {
		if (this.muted) return
		const ctx = this.getContext()
		if (!ctx || (opts?.dropIfSuspended && ctx.state === 'suspended')) return
		this.loadSample(name)
			.then(buffer => {
				// dispose() may have closed ctx during the decode. Its master bus would
				// be rebuilt on the closed context and every later voice would fail to
				// connect to it, so the cue is dropped.
				if (!buffer || this.muted || ctx !== this.audioContext) return
				const src = ctx.createBufferSource()
				src.buffer = buffer
				// Per-fire humanization: ±4 cents pitch + ±0.5 dB level, so a cue heard
				// dozens of times an hour never reads as one mechanically identical
				// sample — the crafted-object tell a static master can't carry itself.
				src.playbackRate.value = 2 ** ((Math.random() * 8 - 4) / 1200)
				const gain = ctx.createGain()
				const jitter = 10 ** ((Math.random() - 0.5) / 20)
				// Relative mix only (theme × per-fire jitter); device volume/mute is the gate.
				gain.gain.value = (this.theme?.masterVolume ?? 1) * jitter
				src.connect(gain).connect(this.getMaster(ctx))
				src.start()
			})
			// A cue is decoration on an action that already happened: whatever stops
			// it leaves silence, never a rejection in the app that fired it.
			.catch(() => undefined)
	}

	// ---- autoplay unlock ---------------------------------------------------

	/**
	 * Resume the AudioContext from within a user gesture and flush any cue that
	 * was armed while still locked. Wire to the first pointerdown/keydown of the
	 * session.
	 */
	public unlock(): void {
		const ctx = this.getContext()
		if (!ctx) return
		const flush = () => {
			this.unlocked = true
			if (this.pendingUnlockCue) {
				const cue = this.pendingUnlockCue
				this.pendingUnlockCue = null
				this.playCue(cue)
			}
		}
		if (ctx.state === 'suspended') {
			ctx.resume().then(flush, () => undefined)
		} else {
			flush()
		}
	}

	/**
	 * Play now if audio is already unlocked, otherwise arm the cue to play on
	 * the next unlock — for the post-sign-in "welcome", which lands with no
	 * gesture of its own.
	 */
	public playCueOnUnlock(name: string): void {
		if (this.unlocked && this.getContext()?.state === 'running') {
			this.playCue(name)
		} else {
			this.pendingUnlockCue = name
		}
	}

	public dispose(): void {
		if (this.audioContext) {
			// Rejects only for a context the platform already closed — nothing left to release.
			this.audioContext.close().catch(() => undefined)
			this.audioContext = null
		}
		this.masterGain = null
		this.sampleBuffers.clear()
		this.unlocked = false
		this.pendingUnlockCue = null
	}

	static getInstance(): SoundService {
		if (!SoundService.instance) {
			SoundService.instance = new SoundService()
		}
		return SoundService.instance
	}
}

export const sound = SoundService.getInstance()
export const schmancySound = sound
